
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Link as LinkIcon, Settings, ChevronLeft, Bell, Sparkles, LogOut, ChevronUp, Users, CalendarClock, Building, BarChart3, PlusCircle, Shield, Gift, HelpCircle, Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarGroupLabel, SidebarSeparator } from "@/components/ui/sidebar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EnergyForecastSidebarCard } from "@/components/EnergyForecastSidebarCard";
import { SidebarTrialProgress } from "@/components/trial/SidebarTrialProgress";
import { useAuth } from "@/lib/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useBillingSubscription } from "@/hooks/use-billing-subscription";
import { Badge } from "@/components/ui/badge";
import { useAdminStatus } from "@/hooks/use-admin-status";
import { useBusinessSlugCheck } from "@/hooks/use-business-slug-check";
import { useTrialUpgrade } from "@/hooks/useTrialUpgrade";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import * as Sentry from "@sentry/react";

// Main navigation items - moved outside component to prevent recreation
const mainNav = [{
  title: "Meeting Links",
  url: "/meeting",
  icon: LinkIcon
}, {
  title: "Bookings",
  url: "/bookings",
  icon: CalendarClock
}, {
  title: "Contacts",
  url: "/contacts",
  icon: Users
}, {
  title: "Analytics",
  url: "/analytics",
  icon: BarChart3
}, {
  title: "Notifications",
  url: "/notifications",
  icon: Bell
}];

// Settings sub-navigation items - moved outside component to prevent recreation
const settingsNav = [
  {
    title: "Profile",
    url: "/settings",
  },
  {
    title: "Plans & Billing",
    url: "/settings/plans",
  },  
  {
    title: "Wearables",
    url: "/settings/wearables",
  },
  {
    title: "Calendar",
    url: "/settings/calendar",
  },
  {
    title: "Availability",
    url: "/settings/availability",
  },
  {
    title: "Notifications",
    url: "/settings/notifications",
  },
  {
    title: "API Keys",
    url: "/settings/api-keys",
  },
  {
    title: "Integrations",
    url: "/settings/integrations",
  },
  {
    title: "Development Tools",
    url: "/settings/development",
  }
];

// Admin sub-navigation items
const adminNav = [
  {
    title: "Health",
    url: "/admin/health",
  },
  {
    title: "Waitlist",
    url: "/admin/waitlist",
  },
  {
    title: "Feature Flags",
    url: "/admin/feature-flags",
  },
  {
    title: "Prospects",
    url: "/admin/prospects",
  },
];

function AppSidebarComponent(props: React.ComponentProps<typeof Sidebar>) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { user } = useAuth();
  
  // Memoized user data to prevent re-renders
  const userData = useMemo(() => ({
    id: user?.id,
    email: user?.email,
    fullName: user?.user_metadata?.full_name,
    avatarUrl: user?.user_metadata?.avatar_url
  }), [user?.id, user?.email, user?.user_metadata?.full_name, user?.user_metadata?.avatar_url]);
  
  const [showSettings, setShowSettings] = useState(location.pathname.startsWith("/settings"));
  const [showAdmin, setShowAdmin] = useState(location.pathname.startsWith("/admin"));
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  // Update showSettings when location changes
  useEffect(() => {
    setShowSettings(location.pathname.startsWith("/settings"));
    setShowAdmin(location.pathname.startsWith("/admin"));
  }, [location.pathname]);
  
  // Memoized hook results to prevent re-renders
  const adminStatus = useAdminStatus();
  const businessSlugCheck = useBusinessSlugCheck();
  const billingSubscription = useBillingSubscription();
  const trialUpgrade = useTrialUpgrade();
  
  const { isAdmin } = adminStatus;
  const { hasBusinessSlug } = businessSlugCheck;
  const { isSubscribed: subscribed, isTrialing: isTrialActive } = billingSubscription;
  const { shouldShowUpgrade, handleUpgrade, isUpgrading } = trialUpgrade;
  
  // Feature flags
  const isReferralProgramEnabled = useFeatureFlag("referral-program");
  const isNotificationsEnabled = useFeatureFlag("notifications");
  const isNotificationBellEnabled = useFeatureFlag("notifications-bell");
  const isNotificationCenterEnabled = useFeatureFlag("notifications-center");
  const isNotificationSettingsEnabled = useFeatureFlag("notifications-settings");
  const isIntegrationsEnabled = useFeatureFlag("integrations");

  // Memoized active states to prevent re-renders
  const activeStates = useMemo(() => {
    const pathname = location.pathname;
    return {
      meeting: pathname.startsWith("/meeting"),
      bookings: pathname.startsWith("/bookings"),
      contacts: pathname.startsWith("/contacts"),
      analytics: pathname.startsWith("/analytics"),
      notifications: pathname.startsWith("/notifications"),
      settings: pathname.startsWith("/settings"),
      admin: pathname.startsWith("/admin")
    };
  }, [location.pathname]);

  // Memoized class names to prevent re-renders
  const getActiveClass = useCallback((isActive: boolean) => 
    `flex items-center gap-2 px-2 py-2 rounded-lg ${isActive ? 'rounded-md bg-gray-100' : 'hover:bg-gray-50 hover:rounded-md'}`, 
    []
  );

  // Memoized event handlers
  const handleSettingsClick = useCallback(() => {
    setShowSettings(true);
    navigate("/settings");
  }, [navigate]);

  const handleBackClick = useCallback(() => {
    if (showSettings) {
      setShowSettings(false);
    }
    if (showAdmin) {
      setShowAdmin(false);
    }
    navigate("/meeting");
  }, [navigate, showSettings, showAdmin]);

  // Updated logout handler to properly handle the logout process
  const handleLogout = useCallback(async () => {
    try {
      // First clear any local state
      console.log("Starting logout process...");
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Error during logout:", error);
        toast.error("Failed to log out. Please try again.");
        return;
      }
      
      console.log("Logout successful");
      toast.success("Logged out successfully");
      
      // Navigate to login page after a short delay to ensure auth state is updated
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 100);
    } catch (error) {
      console.error("Exception during logout:", error);
      toast.error("Failed to log out. Please try again.");
    }
  }, [navigate]);

  // Handle navigating to help center
  const handleHelpClick = useCallback(() => {
    navigate("/help");
  }, [navigate]);

  const handleReportBug = useCallback(async () => {
    try {
      // Get the feedback instance
      const feedback = Sentry.getFeedback();
      
      if (feedback) {
        // Create and show the feedback form
        const form = await feedback.createForm({
          formTitle: "Report a Bug",
          submitButtonLabel: "Send Bug Report",
          nameLabel: "Name",
          namePlaceholder: "Your name",
          emailLabel: "Email", 
          emailPlaceholder: "your.email@example.com",
          messageLabel: "Description",
          messagePlaceholder: "What's the bug? What did you expect?",
        });
        
        form.appendToDom();
        form.open();
      } else {
        console.error('Sentry feedback not available');
        toast.error('Feedback system not available');
      }
    } catch (error) {
      console.error('Error opening feedback form:', error);
      toast.error('Failed to open feedback form');
    }
  }, []);



  // Memoized filtered settings navigation
  const filteredSettingsNav = useMemo(() => 
    settingsNav.filter(item => {
      // Filter out Development Tools for non-admins
      if (item.title === "Development Tools" && !isAdmin) return false;
      // Filter out Notifications if feature is disabled
      if (item.title === "Notifications" && !isNotificationSettingsEnabled) return false;
      // Filter out Integrations if feature is disabled
      if (item.title === "Integrations" && !isIntegrationsEnabled) return false;
      return true;
    }),
    [isAdmin, isNotificationSettingsEnabled, isIntegrationsEnabled]
  );

  // Memoized user display name
  const userDisplayName = useMemo(() => 
    userData.fullName || "User",
    [userData.fullName]
  );

  // Memoized user email
  const userEmail = useMemo(() => 
    userData.email || "",
    [userData.email]
  );

  // Memoized user avatar
  const userAvatar = useMemo(() => 
    userData.avatarUrl || "",
    [userData.avatarUrl]
  );

  // Memoized user initials
  const userInitials = useMemo(() => 
    userDisplayName[0] ?? "U",
    [userDisplayName]
  );

  return (
    <Sidebar collapsible="offcanvas" className="bg-stone-white dark:bg-slate-950 border-r border-border h-screen sidebar-notification-badge" {...props}>
      <SidebarHeader className="p-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center justify-between w-full">
              <Link to="/" className="flex items-center">
                <img 
                  src="/assets/icons/branding/chronocal-logo.svg" 
                  alt="ChronoCal Logo" 
                  className="h-5 w-auto"
                />
              </Link>
            </div>
          </SidebarMenuItem>
          {(showSettings || showAdmin) && (
            <SidebarMenuItem className="mt-4">
              <SidebarMenuButton onClick={handleBackClick} className="flex items-center gap-3 px-2 py-1.5">
                <ChevronLeft className="h-5 w-5" />
                <span>Back</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="overflow-y-auto">
        {/* Search at the top - temporarily hidden */}
        {/* <div className="px-3 pb-2">
          <SearchButton />
        </div>
        
        <SidebarSeparator className="my-2" /> */}

        {showSettings ? (
          <SidebarMenu className="px-3">
            {filteredSettingsNav.map(item => {
              const isItemActive = item.url === "/settings" ? location.pathname === "/settings" : location.pathname === item.url;
              return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={isItemActive}
                >
                  <Link to={item.url} className={getActiveClass(isItemActive) + " font-medium"}>
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )})}
          </SidebarMenu>
        ) : showAdmin ? (
          <SidebarMenu className="px-3">
            {adminNav.map(item => {
              const isItemActive = location.pathname === item.url;
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isItemActive}>
                    <Link to={item.url} className={getActiveClass(isItemActive) + " font-medium"}>
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        ) : (
          <>
            <div className="flex items-center gap-2 px-3 pb-2 pr-6 overflow-visible">
              <Button 
                variant="default" 
                size="sm" 
                onClick={() => navigate("/meeting/create")} 
                disabled={!hasBusinessSlug}
                className="whitespace-nowrap font-normal px-3 py-1.5 h-fit w-fit flex-shrink-0"
                title={!hasBusinessSlug ? "Set up your business profile first" : "Create a new meeting link"}
              >
                <span className="flex items-center gap-2 font-semibold">
                  <PlusCircle className="h-3 w-3" />
                  Meeting Link
                </span>
              </Button>
              {isNotificationBellEnabled && (
                <div className="flex-shrink-0">
                  <NotificationBell />
                </div>
              )}
            </div>

            <SidebarMenu className="mt-2 px-3">
              {mainNav.map(item => {
                const isItemActive = item.url === "/meeting" ? activeStates.meeting :
                                   item.url === "/bookings" ? activeStates.bookings :
                                   item.url === "/contacts" ? activeStates.contacts :
                                   item.url === "/analytics" ? activeStates.analytics :
                                   item.url === "/notifications" ? activeStates.notifications : false;
                
                // Skip notifications if the feature is disabled
                if (item.url === "/notifications" && !isNotificationCenterEnabled) {
                  return null;
                }
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isItemActive}>
                      <Link to={item.url} className={getActiveClass(isItemActive)}>
                        <item.icon className="h-5 w-5" />
                        <span className="font-medium">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>

            <div className="flex-1" />

            <EnergyForecastSidebarCard />
            <SidebarTrialProgress />


            {/* <SidebarSeparator className="my-4" /> */}
            <SidebarMenu className="px-3">
              {/* <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleSettingsClick}
                  isActive={activeStates.settings}
                  className={getActiveClass(activeStates.settings)}
                >
                  <Settings className="h-5 w-5" />
                  <span>Settings</span>
                </SidebarMenuButton>
              </SidebarMenuItem> */}
              
              {/* Admin link - only show for admin users */}
              {isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={activeStates.admin}
                    className={getActiveClass(activeStates.admin)}
                  >
                    <Link to="/admin">
                      <Shield className="h-5 w-5" />
                      <span>Admin</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </>
        )}
      </SidebarContent>

      <SidebarFooter className="flex flex-col gap-3 items-stretch pb-1 px-1">
        <div className="flex items-center justify-between">
          {user && (
            <Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 w-full p-2 rounded-md hover:bg-muted group">
                  <Avatar className="h- 0 w-10 border-2 border-transparent group-hover:border-primary transition-all">
                    <AvatarImage 
                      src={userAvatar} 
                      alt={userDisplayName} 
                      className="object-cover rounded-full"
                    />
                    <AvatarFallback className="rounded-full">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 text-left">
                    <div className="font-medium text-sm leading-tight truncate">
                      {userDisplayName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {userEmail}
                    </div>
                  </div>
                  <ChevronUp className="h-4 w-4 ml-auto text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start" side="top">
                <div className="flex items-center gap-3 p-4 border-b">
                  <Avatar className="h-12 w-12 border-2 border-primary">
                    <AvatarImage 
                      src={userAvatar} 
                      alt={userDisplayName} 
                      className="object-cover rounded-full"
                    />
                    <AvatarFallback className="rounded-full">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm leading-tight truncate">
                      {userDisplayName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {userEmail}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge variant={subscribed ? "default" : "secondary"} className="text-xs">
                        {subscribed ? "Pro" : "Trial"}
                      </Badge>
                      {shouldShowUpgrade && (
                        <Button
                          onClick={() => {
                            handleUpgrade();
                            setIsUserMenuOpen(false);
                          }}
                          disabled={isUpgrading}
                          size="sm"
                          className="h-5 px-2 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-white font-medium shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200 border-0 text-xs"
                        >
                          <Sparkles className="h-2 w-2 mr-0" />
                          {isUpgrading ? '...' : 'Upgrade'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 px-4 py-2 h-auto"
                    onClick={() => {
                      setShowSettings(true);
                      navigate("/settings");
                      setIsUserMenuOpen(false);
                    }}
                  >
                    <Users className="h-4 w-4" />
                    <span>Profile</span>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 px-4 py-2 h-auto"
                    onClick={() => {
                      setShowSettings(true);
                      navigate("/settings");
                      setIsUserMenuOpen(false);
                    }}
                  >
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 px-4 py-2 h-auto"
                    onClick={() => {
                      handleHelpClick();
                      setIsUserMenuOpen(false);
                    }}
                  >
                    <HelpCircle className="h-4 w-4" />
                    <span>Help</span>
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 px-4 py-2 h-auto"
                    onClick={() => {
                      handleReportBug();
                      setIsUserMenuOpen(false);
                    }}
                  >
                    <Bug className="h-4 w-4" />
                    <span>Report a bug</span>
                  </Button>
                  {isReferralProgramEnabled && (
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2 px-4 py-2 h-auto"
                      onClick={() => {
                        navigate("/affiliates");
                        setIsUserMenuOpen(false);
                      }}
                    >
                      <Gift className="h-4 w-4" />
                      <span>Earn 50% referral</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 px-4 py-2 h-auto text-red-600 hover:text-red-600 hover:bg-red-50"
                    onClick={() => {
                      handleLogout();
                      setIsUserMenuOpen(false);
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Log out</span>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

// Export memoized component to prevent unnecessary re-renders
export default React.memo(AppSidebarComponent);
